import WidgetRequest from "api/lib/data/interfaces/WidgetRequest";
import WidgetResult from "api/lib/data/interfaces/WidgetResult";
import Widget from "api/lib/data/Widget";
import * as React from "react";
import * as _ from "lodash";
import moment from "moment";
import { Layout, Layouts } from "react-grid-layout";
import { Card, Icon, Placeholder, Popup } from "semantic-ui-react";
import { Base, Event, IBaseProps, IBaseState } from "../Common/Base";
import * as routes from "../../constants/routes";
import Query from "api/lib/data/Query";

const DashboardWidget = React.lazy(
  () =>
    import(
      /* webpackChunkName: "DashboardWidget" */ "../Visualization/DashboardWidget"
    )
);
const timeFromNow = (timestamp: object) => moment(timestamp).fromNow();

export interface IWidgetLayoutProps extends IBaseProps {
  widget: Widget;
  w: number;
  h: number;
}

export interface IWidgetLayoutState extends IBaseState {
  id: string;
  title: string;
  request: WidgetRequest;
  result: WidgetResult;
  loading: boolean;
  isOutDated: boolean;
  latestVersion: string;
}

export default class WidgetLayout extends Base<
  IWidgetLayoutProps,
  IWidgetLayoutState
> {
  timer = null;
  private static INITAL_STATE = {
    id: null,
    title: null,
    request: null,
    result: null,
    loading: true,
    isOutDated: false,
    latestVersion: "",
  };
  constructor(props: IWidgetLayoutProps) {
    super(props);

    this.state = { ...Base.BASE_INITAL_STATE, ...WidgetLayout.INITAL_STATE };
  }

  public render() {
    const {
      title,
      request,
      result,
      isOutDated,
      latestVersion,
      loading,
    } = this.state;
    return (
      <React.Fragment>
        <Card>
          <Card.Content
            style={{
              padding: "6px",
              backgroundColor: "rgba(0,0,0,.03)",
              borderRadius: "0 !important",
            }}
          >
            {title && (
              <Card.Header style={{ fontWeight: "500", fontSize: "1.2em" }}>
                {title}
              </Card.Header>
            )}
            <Icon
              style={{
                position: "absolute",
                right: "0px",
                top: "5px",
                color: "rgba(0,0,0,0.5)",
              }}
              link={true}
              name="close"
              onClick={this.handleRemoveWidget}
            />
            {latestVersion && (
              <Popup
                position="bottom left"
                content="Show query"
                trigger={
                  <Icon
                    style={{
                      position: "absolute",
                      right: "20px",
                      top: "5px",
                      color: "rgba(0,0,0,0.5)",
                    }}
                    name="window maximize outline"
                    link={true}
                    onClick={this.handleGoToQuery}
                  />
                }
              />
            )}
            {isOutDated && (
              <Popup
                position="bottom left"
                content="This query has newer version"
                trigger={
                  <Icon
                    style={{
                      position: "absolute",
                      right: "40px",
                      top: "5px",
                      color: "rgba(0,0,0,0.5)",
                    }}
                    name="warning circle"
                    link={true}
                    color="orange"
                  />
                }
              />
            )}
          </Card.Content>
          <Card.Content className="widget-content" style={{ border: "none" }}>
            {result === null && (
              <Placeholder>
                <Placeholder.Image square={true} />
              </Placeholder>
            )}
            {request && result && (
              <React.Suspense
                fallback={
                  <Placeholder>
                    <Placeholder.Image square={true} />
                  </Placeholder>
                }
              >
                <DashboardWidget
                  selectedVisualization={request.selectedVisualization}
                  data={result}
                />
              </React.Suspense>
            )}
            {result && result.executedAt && (
              <div
                style={{
                  position: "absolute",
                  bottom: "0",
                  right: "15px",
                }}
              >
                <span
                  style={{
                    fontSize: "12px",
                    color: "rgba(0,0,0,.3)",
                    marginRight: "6px",
                  }}
                >
                  last run {timeFromNow(result.executedAt)}
                </span>
                <Popup
                  content="refresh data"
                  trigger={
                    <Icon
                      name="refresh"
                      link={true}
                      loading={loading}
                      onClick={this.handleRefreshWidget}
                    />
                  }
                />
              </div>
            )}
          </Card.Content>
        </Card>
      </React.Fragment>
    );
  }

  public componentDidMount = () => {
    this.getData();
  };

  public componentDidUpdate = (prevProps, prevState) => {
    if (this.props.w !== prevProps.w || this.props.h !== prevProps.h) {
      this.setState({ result: null });
      this.getResult();
    }
  };

  public componentWillUnmount = () => {
    clearTimeout(this.timer);
  };

  private getData = () => {
    const { widget } = this.props;

    this.getOnceListner(
      widget.get(),
      Event.Value,
      (snapshot: firebase.database.DataSnapshot) => {
        const widgetObj = snapshot.val() || {};
        this.checkLatestVersion(widgetObj.request);
        this.setState({
          title: widgetObj.config.name || "",
          request: widgetObj.request,
        });
      }
    );
    this.getResult();
  };

  private getResult = () => {
    const { widget } = this.props;
    this.getOnceListner(widget.getResult(), Event.Value, (resultSnap) => {
      this.timer = setTimeout(() => {
        this.setState({
          result: resultSnap.val(),
          loading: false,
        });
      }, 250);
    });
  };

  private handleGoToQuery = () => {
    const { orgId, history } = this.props;
    const { request } = this.state;
    history.push(
      routes.QUERY_BY_ID(orgId, request.analysisId, request.queryId)
    );
  };

  private checkLatestVersion = (req: WidgetRequest) => {
    const query = new Query(this.props.orgId, req.analysisId, req.queryId);
    this.getListner(
      query.getVersions().orderByKey().limitToLast(1),
      Event.Value,
      (versionSnapshot) => {
        const latestVersion: any = versionSnapshot.val();
        if (latestVersion) {
          const latestVersionKey = Object.keys(latestVersion)[0];
          this.setState({
            isOutDated: latestVersionKey !== req.versionId,
            latestVersion: latestVersionKey,
          });
        }
      }
    );
  };

  private handleRemoveWidget = () => {
    const { widget } = this.props;
    const dashboard = widget.getDashboard();
    this.getOnceListner(
      dashboard.getLayout(widget.getDashboardId()),
      Event.Value,
      (layoutSnap) => {
        const layouts: Layouts =
          JSON.parse((layoutSnap.val() || {}).val || "{}") || {};
        ["lg", "md", "sm", "xs", "xxs"].forEach((breakpnt) => {
          const layout: Layout[] = layouts[breakpnt] || [];
          layouts[breakpnt] = _.reject(layout, (o) => o.i === widget.id);
        });
        dashboard
          .updateLayout(widget.getDashboardId(), {
            val: JSON.stringify(layouts),
          })
          .then(() => {
            widget.delete().then(() => {
              console.log("widget deleted");
            });
          });
      }
    );
  };

  private handleRefreshWidget = () => {
    const { widget } = this.props;
    const { request } = this.state;
    widget.refreshWidget(request).then(() => {
      this.setState({ loading: true });
      this.getResult();
    });
  };
}



// WEBPACK FOOTER //
// ./src/components/Dashboard/WidgetLayout.tsx